import React, { useEffect } from 'react';

// Reusable Image Modal Component
// Designed for single-image viewing (Floorplan, profile pictures, documents, etc.)
const ImageModal = ({
  isOpen,
  onClose,
  imageUrl,
  imageAlt,
  title,
  showDownload = false,
  onDownload
}) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else if (imageUrl) {
      // Default download behavior
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = title ? `${title.toLowerCase().replace(/\s+/g, '-')}.png` : 'image.png';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"
      onClick={onClose}
    >
      <div className="relative w-full h-full flex items-center justify-center p-4" onClick={e => e.stopPropagation()}>
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close image viewer"
          className="absolute top-4 right-4 z-10 group"
        >
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/20 backdrop-blur-md border border-white/20 shadow-lg transition-all duration-200 group-hover:bg-white/30 group-hover:scale-105">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </span>
        </button>

        {/* Download button */}
        {showDownload && (
          <button
            onClick={handleDownload}
            aria-label="Download image"
            className="absolute top-4 left-4 z-10 group"
          >
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/20 backdrop-blur-md border border-white/20 shadow-lg transition-all duration-200 group-hover:bg-white/30 group-hover:scale-105">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </span>
          </button>
        )}

        {/* Image container */}
        <div className="relative max-w-full max-h-full">
          <img
            src={imageUrl}
            alt={imageAlt}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            style={{ maxHeight: '90vh', maxWidth: '90vw' }}
          />
        </div>

        {/* Title overlay */}
        {title && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
            <div className="bg-black/60 backdrop-blur-md px-4 py-2 rounded-lg">
              <p className="text-white text-center font-medium">{title}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageModal;

// Usage Examples:
//
// 1. Simple floorplan viewing (current usage):
// <ImageModal
//   isOpen={isModalOpen}
//   onClose={closeFloorplanModal}
//   imageUrl={floorplanUrl}
//   imageAlt="Wedding venue floorplan"
//   title="Wedding Venue Floorplan"
// />
//
// 2. Profile picture enlargement:
// <ImageModal
//   isOpen={showProfilePic}
//   onClose={() => setShowProfilePic(false)}
//   imageUrl={profilePictureUrl}
//   imageAlt={`${userName}'s profile picture`}
//   title={`${userName}'s Profile`}
// />
//
// 3. Document viewing with download:
// <ImageModal
//   isOpen={showCertificate}
//   onClose={() => setShowCertificate(false)}
//   imageUrl={certificateUrl}
//   imageAlt="Wedding certificate"
//   title="Wedding Certificate"
//   showDownload={true}
//   onDownload={handleDownloadCertificate}
// />
